import React, { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { ChildrenStoreData } from 'src/shared/types/src';
import { useAppDispatch, useAppSelector } from '../../services/store';
import { formatUnixTimestamp } from 'src/shared/utils/src';
import LocalImages from "../../static";
import InvoiceDetailsModal from './InvoiceDetailsModal';
import DepositPaymentDetailsModal from './DepositPaymentDetailsModal';
import PaymentModal from './PaymentModal';
import { fetchTransactions, getAccountBalance, getAccountDeposit, getBillPayer, getCurrency, getCurrentChild, getPaymentGatewayConfig, getTransactions, getTransactionsLoading } from 'src/shared/redux-state/src';
import { TransactionType } from 'src/shared/types/src/lib/invoice.types'
import { APP_MESSAGES } from 'src/shared/config/src';
import { CustomButton, InvoiceLoader } from 'src/app/components';
import { BaseCard } from 'src/app/components';
import { toast } from "react-toastify";
import ProfileLayout from 'src/app/layouts/ProfileLayout';
import Images from '../../static';
import { navigateToDownloadAppScreen } from 'src/app/utilities/helpers';
import { GoArrowSwitch } from "react-icons/go";
import CustomAmountComponent from 'src/app/components/CustomAmountComponent';

type paymentGatewayConfigType = {
    connected: boolean,
    type: string | null
}

const InvoiceScreen = () => {

    const dispatch = useAppDispatch();

    const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);
    const accountBalance: string = useAppSelector(getAccountBalance);
    const accountDeposit: string = useAppSelector(getAccountDeposit);
    const currency: string = useAppSelector(getCurrency);
    const transactions: TransactionType[] = useAppSelector(getTransactions);
    const paymentGatewayConfig: paymentGatewayConfigType = useAppSelector(getPaymentGatewayConfig);
    const transactionsLoading: boolean = useAppSelector(getTransactionsLoading);
    const isBillPayer = useAppSelector(getBillPayer);

    const [currentItem, setCurrentItem] = useState<TransactionType>();
    const [isInvoiceDetailsModal, setIsInvoiceDetailsModal] = useState<boolean>(false);
    const [isDepositPaymentDetailsModal, setIsDepositPaymentDetailsModal] = useState<boolean>(false);
    const [isPaymentModal, setIsPaymentModal] = useState<boolean>(false);

    const TYPE_PAYMENT = 'Payment';
    const TYPE_INVOICE = 'Invoice';
    const TYPE_DEPOSIT = 'Deposit'
    const TYPE_REFUND = 'Refund/Transfer';
    const TYPE_CREDIT_NOTE = 'Credit Note';

    useEffect(() => {
        init()
    }, []);

    const init = async () => {
        try {
            await dispatch(fetchTransactions({ child: currentChild }));
        } catch (error) {
            handleApiErrors(error);
        }
    };

    const handleApiErrors = (error: any, message?: string) => {
        if (error instanceof AxiosError) {
            if (error && error.response) {
                const { errors } = error.response.data;
                toast.error(errors && errors.length ? errors.join(', ') : errors[0]);
            } else {
                toast.error(message || APP_MESSAGES.generalErrorMessage)
            }
        } else {
            toast.error(message || APP_MESSAGES.generalErrorMessage)
        }
    }

    const getCardIcon = (type: string) => {
        switch (type) {
            case TYPE_PAYMENT:
                return <img src={Images.payment} alt="payment-icon" />
            case TYPE_INVOICE:
                return <img src={Images.invoiceImg} alt="invoice-icon" />
            case TYPE_REFUND:
                return <GoArrowSwitch />
            case TYPE_CREDIT_NOTE:
                return <img src={Images.invoicePaymentIcon} alt="invoice-payment-icon" />
            default:
                return <img src={Images.deposit} alt="deposit-icon" />
        }
    };

    const toggleInvoiceDetailsModal = () => {
        setIsInvoiceDetailsModal(!isInvoiceDetailsModal);
    }

    const toggleDepositPaymentDetailsModal = () => {
        setIsDepositPaymentDetailsModal(!isDepositPaymentDetailsModal);
    }

    const togglePaymentModal = () => {
        setIsPaymentModal(!isPaymentModal);
    }

    const renderCards = (item: TransactionType) => {
        let title = item.type;

        switch (item?.type) {
            case TYPE_PAYMENT:
                title = "Payment";
                break;
            case TYPE_INVOICE:
                title = "Invoice";
                break;
            case TYPE_DEPOSIT:
                title = "Deposit";
                break;
            default:
                title = item?.type;
        };

        return (
            <BaseCard
                clickable={true}
                onPressFunc={() => {
                    setCurrentItem(item)
                    if (item?.type === TYPE_INVOICE) {
                        toggleInvoiceDetailsModal()
                    } else {
                        toggleDepositPaymentDetailsModal()
                    }
                }}
                customBaseCardStyle='pd-15 mg-b-15'
            >
                <div className='pd-l-45 position-relative d-flex align-items-center'>
                    <div className='invoice-payment-icon border border-gray-200 position-absolute start-0 d-inline-flex align-items-center justify-content-center rounded-circle'>
                        {getCardIcon(item?.type)}
                    </div>
                    <div className='d-flex flex-column flex-1 pd-r-10'>
                        <div className='fw-semibold fs-14 text-black' style={{ wordBreak: 'break-word' }}>{title}
                            {item?.type === TYPE_INVOICE ? `(${item?.details?.txid})` : ''}
                        </div>
                        <div className='fs-12 text-black d-flex'>
                            Balance:&nbsp;<CustomAmountComponent amount={item?.balance} currency={currency} />
                        </div>
                    </div>
                    <div className='text-end'>
                        <div className='fs-12 text-gray-300'>{formatUnixTimestamp(item?.timestamp)}</div>
                        <div className='fs-12 fw-bold text-black'><CustomAmountComponent amount={item?.amount} currency={currency} /></div>
                    </div>
                </div>
            </BaseCard>
        )
    };

    const callbackOnSuccess = () => {
        init();
    }

    const _mountModals = () => {
        return (
            <>
                <InvoiceDetailsModal
                    invoiceData={currentItem?.details}
                    currency={currency}
                    isInvoiceDetailsModal={isInvoiceDetailsModal}
                    toggleInvoiceDetailsModal={toggleInvoiceDetailsModal}
                />

                <DepositPaymentDetailsModal
                    paymentData={currentItem?.details}
                    currency={currency}
                    isDepositPaymentDetailsModal={isDepositPaymentDetailsModal}
                    toggleDepositPaymentDetailsModal={toggleDepositPaymentDetailsModal}
                />

                <PaymentModal
                    currency={currency}
                    balance={accountBalance}
                    type={paymentGatewayConfig?.type}
                    callbackOnSuccess={callbackOnSuccess}
                    isPaymentModal={isPaymentModal}
                    togglePaymentModal={togglePaymentModal}
                />
            </>
        )
    }

    const _renderAccountInfoCard = () => {
        return (
            <div className='mg-t-20'>
                <BaseCard
                    customBaseCardStyle='pd-15 mg-b-10'
                >
                    <div className='position-relative'>
                        <div className='d-flex align-items-center'>
                            <div className='fs-14 text-black flex-1'>Account Balance</div>
                            <div className='fs-20 fw-semibold text-black text-end'>
                                {accountBalance ?
                                    <CustomAmountComponent
                                        amount={accountBalance}
                                        currency={currency}
                                        applyFormat={true}
                                        applyColor={true}
                                    /> : ''
                                }
                            </div>
                        </div>
                        <div className='d-flex align-items-center mg-t-20'>
                            <div className='fs-14 text-black flex-1'>
                                Deposit Held
                            </div>
                            <div className='fs-14 fw-semibold text-black text-end'>
                                {accountDeposit ?
                                    <CustomAmountComponent
                                        amount={accountDeposit}
                                        currency={currency}
                                        applyFormat={true}
                                        withSign={false}
                                    /> : ''
                                }
                            </div>
                        </div>
                    </div>
                    {(isBillPayer && paymentGatewayConfig?.connected) ?
                        <div className='d-flex align-items-center mg-t-20'>
                            <CustomButton
                                onClick={navigateToDownloadAppScreen}
                                icon={<img style={{ width: '20px' }} src={Images.creditCardsIcon} alt="credit-cards-icon" />}
                                color="outline-primary"
                                customBtnStyle={`d-flex align-items-center justify-content-center p-0
                                    ${!transactionsLoading && !(isBillPayer && paymentGatewayConfig?.connected) ? '' : 'mg-r-15 w45'}`
                                }
                                disabled={transactionsLoading}
                            />
                            <CustomButton
                                onClick={navigateToDownloadAppScreen}
                                text='Pay Now'
                                color="primary"
                                disabled={transactionsLoading}
                            />
                        </div> : null
                    }
                </BaseCard>
            </div>
        )
    }

    if (!transactionsLoading && !isBillPayer) {
        return (
            <ProfileLayout title="Invoice and Payments">
                <div className='d-flex align-items-center justify-content-center flex-column flex-grow-1'>
                    <img src={LocalImages.notABillPayer} alt="notABillPayer" style={{ width: "100px", height: "100px" }} />
                    <p className='text-center mb-0 fs-6 fw-semibold mg-t-20'>You have not been added as a bill payer</p>
                </div>
            </ProfileLayout>
        )
    }

    return (
        <ProfileLayout title="Invoice and Payments">
            {_mountModals()}
            {transactionsLoading && <InvoiceLoader />}

            {(!transactionsLoading && transactions?.length === 0) ?
                <div className='d-flex align-items-center justify-content-center flex-column flex-grow-1'>
                    <img src={LocalImages.invoice} alt="notABillPayer" style={{ width: "100px", height: "100px" }} />
                    <p className='text-center mb-0 fs-6 fw-semibold mg-t-20'>No transactions found</p>
                </div> : null
            }

            {(!transactionsLoading && isBillPayer && transactions?.length) ?
                <>
                    {_renderAccountInfoCard()}
                    <div className='mg-t-20 flex-1'>
                        <p className='fs-6 fw-semibold text-black mb-2'>Transactions</p>
                        <div className='transaction-list-wrapper' style={{ maxHeight: !(isBillPayer && paymentGatewayConfig?.connected) ? 'calc(100vh - 300px)' : 'calc(100vh - 360px)' }}>
                            {transactions.map(item => {
                                return renderCards(item);
                            })}
                        </div>
                    </div> 
                </> : null
            }
        </ProfileLayout>
    )
};

export default InvoiceScreen;